import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { TutorRole } from '../services/usersService';

interface RoleEditorDialogProps {
  open: boolean;
  /** Tutor being edited. `null` collapses the dialog without rendering. */
  tutor: {
    userId: string;
    displayName: string;
    email: string;
    role: TutorRole;
  } | null;
  /** Whether the caller's PUT request is currently in flight. */
  saving?: boolean;
  /** Submit the chosen role. Caller handles optimistic update + rollback. */
  onSubmit: (role: TutorRole) => void;
  onClose: () => void;
}

const ROLE_OPTIONS: { value: TutorRole; label: string; description: string }[] = [
  {
    value: 'viewer',
    label: 'Viewer',
    description: 'Nur Lesezugriff — sieht Runs / Logs aber loest keine Aktionen aus.',
  },
  {
    value: 'operator',
    label: 'Operator',
    description: 'Operative Aktionen (VMs starten / stoppen, Passwoerter, Teilnehmer).',
  },
  {
    value: 'admin',
    label: 'Admin',
    description: 'Vollzugriff inkl. Rollen-Verwaltung, Audit-Log, Azure Resources.',
  },
];

export function RoleEditorDialog({
  open,
  tutor,
  saving = false,
  onSubmit,
  onClose,
}: RoleEditorDialogProps) {
  const [selectedRole, setSelectedRole] = useState<TutorRole>('viewer');
  const [confirmingDowngrade, setConfirmingDowngrade] = useState(false);

  // Reset state when the dialog opens for a new tutor — avoids leaking the
  // previous tutor's selection into the next render.
  useEffect(() => {
    if (open && tutor) {
      setSelectedRole(tutor.role);
      setConfirmingDowngrade(false);
    }
  }, [open, tutor]);

  if (!tutor) return null;

  const isAdminDowngrade =
    tutor.role === 'admin' && selectedRole !== 'admin';
  const unchanged = selectedRole === tutor.role;

  const handlePrimaryClick = () => {
    if (isAdminDowngrade && !confirmingDowngrade) {
      // First click on a downgrade-from-admin: enter confirmation stage.
      // The actual API call only fires after a second deliberate click,
      // matching the spec's "two-stage confirmation" requirement.
      setConfirmingDowngrade(true);
      return;
    }
    onSubmit(selectedRole);
  };

  const primaryLabel = (() => {
    if (saving) return 'Speichern…';
    if (isAdminDowngrade && !confirmingDowngrade) return 'Weiter';
    if (isAdminDowngrade && confirmingDowngrade) return 'Admin-Zugriff entziehen';
    return 'Speichern';
  })();

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="role-editor-dialog-title"
    >
      <DialogTitle id="role-editor-dialog-title">
        Rolle bearbeiten — {tutor.displayName}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {tutor.email} · aktuell <strong>{tutor.role}</strong>
        </DialogContentText>

        <FormControl fullWidth disabled={saving}>
          <InputLabel id="role-editor-select-label">Neue Rolle</InputLabel>
          <Select
            labelId="role-editor-select-label"
            label="Neue Rolle"
            value={selectedRole}
            onChange={(e) => {
              setSelectedRole(e.target.value as TutorRole);
              // Any change resets the downgrade confirmation so the
              // operator can re-evaluate after switching the dropdown.
              setConfirmingDowngrade(false);
            }}
            inputProps={{ 'aria-label': 'Neue Rolle' }}
          >
            {ROLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {ROLE_OPTIONS.find((opt) => opt.value === selectedRole)?.description}
        </Typography>

        {isAdminDowngrade && confirmingDowngrade && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Admin-Zugriff entziehen?</strong>
            <br />
            {tutor.displayName} verliert dadurch die Rechte fuer Rollen-Verwaltung,
            Audit-Log und Azure Resources. Diese Aktion kann nur von einem anderen
            Admin rueckgaengig gemacht werden.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          onClick={handlePrimaryClick}
          variant="contained"
          color={isAdminDowngrade && confirmingDowngrade ? 'warning' : 'primary'}
          disabled={saving || unchanged}
          startIcon={saving ? <CircularProgress size={18} /> : undefined}
        >
          {primaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RoleEditorDialog;
